import { create, CLIENT_ERROR, SERVER_ERROR, TIMEOUT_ERROR, NETWORK_ERROR } from 'apisauce';
import type { ApisauceInstance, ApiResponse } from 'apisauce';
import { DOMAIN, API_BASE_URL, USE_FEDERATED_AUTH } from 'config';

import { object, string, array } from 'yup';
import { ErrorType } from '../types';
import { logoutFn } from 'features/auth';
import { SELECTED_USER_ID_KEY } from 'features/auth/hooks/selectedUserContext';
import type { CombinedTypes } from 'features/notifications/types';
import type { PaginateResponse } from 'types';
import { t } from '@lingui/macro';

// The setDelogueApi token is set after a successful login with Auth0
// Needed for API instances, to enable setting authorization header
let delogueApiToken: string | null = null;

export const setDelogueApiToken = (token: string | null) => {
  delogueApiToken = token;
};

export const isDelogueApiEnabled = () => delogueApiToken !== null;

const getSelectedUserId = (): string | null => {
  const raw = localStorage.getItem(SELECTED_USER_ID_KEY);
  return raw ? JSON.parse(raw) : null;
};

/**
 * Legacy API instance - uses DOMAIN (e.g., http://localhost:1105)
 * This is the main API for most application endpoints.
 */
export const api = (() => {
  const instance = create({
    baseURL: DOMAIN,
    headers: {
      Accept: 'application/json',
      'Cache-Control': 'no-cache',
      'Access-Control-Allow-Origin': '*',
    },
    timeout: 180000,
    withCredentials: !USE_FEDERATED_AUTH, //For Auth0 JWT based auth, we don't need to send cookies
  });

  instance.addRequestTransform(request => {
    if (delogueApiToken) {
      request.headers = request.headers || {};
      request.headers.Authorization = `Bearer ${delogueApiToken}`;
    }
    const userId = getSelectedUserId();
    if (userId) {
      request.headers = request.headers || {};
      request.headers['X-User-Id'] = userId;
    }
  });

  const authMonitor = async (response: ApiResponse<unknown>) => {
    // log user out if unauthorized
    if (response.status === 401) {
      await logoutFn();
      // use native navigate to reset all session state
      window.location.replace('/');
    }
  };
  instance.addMonitor(authMonitor);

  return instance;
})();

/**
 * New Delogue.Api instance - uses API_BASE_URL (e.g., https://api.delogue.com)
 * This is the API to use going forward for all API requests.
 */
export const delogueApi = (() => {
  const instance = create({
    baseURL: API_BASE_URL,
    headers: {
      Accept: 'application/json',
      'Cache-Control': 'no-cache',
      'Access-Control-Allow-Origin': '*',
    },
    timeout: 180000,
  });

  // Add bearer token and selected user ID to requests
  instance.addRequestTransform(request => {
    if (delogueApiToken) {
      request.headers = request.headers || {};
      request.headers.Authorization = `Bearer ${delogueApiToken}`;
    }
    const userId = getSelectedUserId();
    if (userId) {
      request.headers = request.headers || {};
      request.headers['X-User-Id'] = userId;
    }
  });

  return instance;
})();

export async function handleRequest<T>(
  promise: Promise<ApiResponse<T, T>>,
  notify?: (notification: CombinedTypes) => void
) {
  const res = await promise;
  if (!res.ok) {
    const errorMessage = await validateError(res as ApiResponse<ErrorType, ErrorType>);
    const message = errorMessage?.code || errorMessage?.msg || '';
    if (notify) {
      const isCode = Boolean(errorMessage?.code);
      notify({ message, id: 'api-error', autoClose: false, isCode });
    }
    throw new Error(message);
  }
  const inValid = validateResponse(res);
  if (inValid || !res.data) {
    throw new Error(inValid);
  }
  return res.data;
}

async function adapterBase(
  promise: Promise<ApiResponse<unknown, unknown>>,
  notify?: (notification: CombinedTypes) => void
) {
  const res = await promise;
  if (!res.ok) {
    const errorMessage = await validateError(res as ApiResponse<ErrorType, ErrorType>);

    const header = errorMessage?.code || errorMessage?.msg || '';
    if (notify) {
      notify({
        header,
        id: 'api-error',
        autoClose: false,
        isCode: Boolean(errorMessage?.code),
        messageList: errorMessage?.messageList,
      });
    }
    throw new Error(header);
  }
  const inValid = validateResponse(res);
  if (inValid || !res.data) {
    throw new Error(inValid);
  }
  return res;
}

export async function adapterPaged<ApiType, TransformedType>(
  promise: Promise<ApiResponse<unknown, unknown>>,
  transform?: (data: ApiType[]) => TransformedType[],
  notify?: (notification: CombinedTypes) => void
): Promise<PaginateResponse<TransformedType> | PaginateResponse<ApiType>> {
  const res = await adapterBase(promise, notify);
  const transformed = res.data as PaginateResponse<ApiType>;
  if (transform) {
    return { ...transformed, data: transform(transformed.data) };
  }
  return transformed;
}

export async function adapter<ApiType, TransformedType>(
  promise: Promise<ApiResponse<unknown, unknown>>,
  transform?: (data: ApiType) => TransformedType,
  notify?: (notification: CombinedTypes) => void
): Promise<TransformedType | ApiType> {
  const res = await adapterBase(promise, notify);
  const transformed = res.data as ApiType;
  if (transform) {
    return transform(transformed);
  }
  return transformed;
}

function validateResponse<T>(res: ApiResponse<T, T>) {
  const contentType = res?.headers?.['content-type'];
  if (res?.config?.method === 'get') {
    if (!contentType?.includes('application/json')) {
      return `Invalid response type (${contentType}), expected application/json.`;
    }
    if (!res?.data) {
      return 'No data in response.';
    }
  }
  return '';
}

const validateError = async (res: ApiResponse<ErrorType, ErrorType>) => {
  if (res.problem === CLIENT_ERROR) {
    const errorMessage = await validateClientError(res as ApiResponse<ErrorType, ErrorType>);
    return errorMessage;
  }
  if (res.problem === SERVER_ERROR) {
    const errorMessage = await validateClientError(res as ApiResponse<ErrorType, ErrorType>);
    return errorMessage;
  }
  if (res.problem === TIMEOUT_ERROR) {
    return {
      msg: `${t`Server didn't respond in time`} (${
        res.config?.timeout ? res.config.timeout / 1000 : 0
      } ${t`seconds`}).`,
    };
  }
  if (res.problem === NETWORK_ERROR) {
    return { msg: t`Internet connection error.` };
  }
  return { msg: `${res.problem}.` };
};

const errorSchema = object({
  code: string().required(),
  status: string().required(),
  data: object().nullable().optional(),
  error: object({
    details: array().of(object({ type: string().required(), value: string().required() })),
  })
    .nullable()
    .optional(),
});

const validateClientError = async (res: ApiResponse<ErrorType, ErrorType>) => {
  const data = res?.data;

  try {
    await errorSchema.validate(data, { abortEarly: false });
  } catch (error) {
    const messages: string[] = [];
    (error as { inner: Error[] }).inner.forEach((innerError: Error) => {
      messages.push(innerError.message);
    });
    return { msg: `Invalid error response. ${messages.join(', ')}` };
  }
  return {
    code: data?.code,
    messageList: data?.error?.details?.map(item => ({
      code: item?.code ?? item?.type,
      msg: item?.value,
    })),
  };
};

/**
 * Converts a camelCase string to PascalCase.
 * @param str - The camelCase string.
 * @returns The PascalCase string.
 */
const camelToPascal = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Recursively converts the keys of an object from camelCase to PascalCase.
 * @param obj - The object with camelCase keys.
 * @returns The object with PascalCase keys.
 */
export const objCamelToPascal = (
  obj: Record<string, unknown> | Record<string, unknown>[]
): Record<string, unknown> | Record<string, unknown>[] => {
  if (Array.isArray(obj)) {
    return obj.map(item => objCamelToPascal(item) as Record<string, unknown>);
  } else if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj).reduce(
      (acc, key) => {
        const pascalKey = camelToPascal(key);
        acc[pascalKey] = objCamelToPascal(obj[key] as Record<string, unknown>);
        return acc;
      },
      {} as Record<string, unknown>
    );
  }
  return obj;
};
